// External / evidence links list — label + URL pairs with add/remove
// controls. Callers can override the field legend via the `label` prop so
// the same component can render as "External Links" (default) or
// "Evidence Links" for the tracking forms without a wrapper duplicating
// the label.

import { Plus, Trash2 } from "lucide-react";
import { Button } from "../common";
import type { ExternalLink } from "../../types";

interface ExternalLinksListProps {
  value: ExternalLink[];
  onChange: (links: ExternalLink[]) => void;
  disabled?: boolean;
  /** Field legend shown above the list. Defaults to "External Links". */
  label?: string;
  /** When set, the *first* row added to an empty list pre-fills its label
   *  with this value (e.g. "CoC" for insurance evidence). Subsequent rows are
   *  always blank — keeps the component generic for non-insurance callers. */
  firstLinkLabel?: string;
}

export default function ExternalLinksList({
  value,
  onChange,
  disabled,
  label = "External Links",
  firstLinkLabel,
}: ExternalLinksListProps) {
  const handleAdd = () => {
    const seededLabel = value.length === 0 ? (firstLinkLabel ?? "") : "";
    onChange([...value, { label: seededLabel, url: "" }]);
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleChange = (
    index: number,
    field: "label" | "url",
    fieldValue: string,
  ) => {
    const updated = [...value];
    updated[index] = { ...updated[index], [field]: fieldValue };
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      <label className="label">{label}</label>
      {value.map((link, index) => (
        <div key={index} className="flex gap-2 items-start">
          <input
            type="text"
            placeholder="Label"
            value={link.label}
            onChange={(e) => handleChange(index, "label", e.target.value)}
            className="input flex-1"
            disabled={disabled}
            aria-label={`Link ${index + 1} label`}
          />
          <input
            type="url"
            placeholder="https://..."
            value={link.url}
            onChange={(e) => handleChange(index, "url", e.target.value)}
            className="input flex-[2]"
            disabled={disabled}
            aria-label={`Link ${index + 1} URL`}
          />
          <Button
            type="button"
            variant="ghost"
            iconOnly
            size="sm"
            icon={<Trash2 className="h-4 w-4" />}
            aria-label={`Remove link ${index + 1}`}
            onClick={() => handleRemove(index)}
            disabled={disabled}
          />
        </div>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        icon={<Plus className="h-4 w-4" />}
        onClick={handleAdd}
        disabled={disabled}
      >
        Add Link
      </Button>
    </div>
  );
}
